import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { IntegrationInfoSection, ModuleCard, ModuleCardSkeletonGrid, ModuleConfigurationDialog } from "../components";
import { useIntegrationModules, useIntegrations } from "../hooks";
import type { IntegrationModule } from "../types";
import { transformModuleToCardData, getStatusBadgeType, getStatusLabelKey } from "../utils";
import { decodeSlug } from "../utils/slug";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/v2/avatar";
import { Badge } from "@/components/ui/v2/badge";
import { ROUTES } from "@/constants/routes";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import { UserRole } from "@/types/user/userType";

function IntegrationDetailPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { integrationName, clientId } = useParams<{ integrationName: string; clientId?: string }>();
  const { tenantId, role, context } = useAuth();
  const [configDialog, setConfigDialog] = useState<{ open: boolean; module: IntegrationModule | null }>({
    open: false,
    module: null,
  });

  // Admin context without a selected client: only SuperAdmin may view tenant-level integrations.
  // Client context (Manager/Spectator) is allowed; backend uses TenantProvider to scope by token.
  useEffect(() => {
    if (context === "admin" && !clientId && role !== UserRole.SUPER_ADMIN) {
      navigate(ROUTES.clients, { replace: true });
    }
  }, [context, clientId, role, navigate]);
  
  // clientId can be from route (Admin context) or undefined (Manager/Spectator context)
  // Backend will use TenantProvider to get clientId from token for Manager/Spectator roles
  const integrationsQuery = useIntegrations({ tenantId, clientId });
  
  const decodedIntegrationName = integrationName
    ? decodeSlug(integrationName)
    : null;
  
  const backendIntegrationName = decodedIntegrationName;

  const integration = integrationsQuery.data?.find(
    (i) => i.name === backendIntegrationName
  ) ?? null;

  const modulesQuery = useIntegrationModules({ 
    integrationName: backendIntegrationName ?? '',
    tenantId,
    clientId,
  });

  // Translate display name if it's a translation key, otherwise use as-is
  // Fallback to backend name if integration not found in list (may still exist on backend)
  const integrationDisplayName = integration?.displayName && i18n.exists(integration.displayName)
    ? t(integration.displayName)
    : integration?.displayName || backendIntegrationName;

  const breadcrumbs = useBreadcrumbs({
    items: clientId 
      ? [
        { label: t("common.integrations"), href: `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}` },
        { label: integrationDisplayName ?? t("common.integration") }
      ]
      : [
        { label: t("common.integrations"), href: `/${ROUTES.integrations}` },
        { label: integrationDisplayName ?? t("common.integration") }
      ]
  });

  const handleConfigure = (moduleId: string) => {
    const module = modulesQuery.data?.find(m => m.id === moduleId);
    if (module) {
      setConfigDialog({ open: true, module });
    }
  };

  const subtitle = integration?.description && i18n.exists(integration.description)
    ? t(integration.description)
    : integration?.description || t("integrations.descriptions.default");

  // Sort modules alphabetically by display name
  const sortedModules = useMemo(() => {
    if (!modulesQuery.data) return [];
    
    return [...modulesQuery.data].sort((a, b) => {
      // Get translated display names for comparison
      const aDisplayName = a.moduleDisplayName && i18n.exists(a.moduleDisplayName)
        ? t(a.moduleDisplayName)
        : a.moduleDisplayName || '';
      const bDisplayName = b.moduleDisplayName && i18n.exists(b.moduleDisplayName)
        ? t(b.moduleDisplayName)
        : b.moduleDisplayName || '';
      
      return aDisplayName.localeCompare(bDisplayName, undefined, { sensitivity: 'base' });
    });
  }, [modulesQuery.data, t, i18n]);

  const renderContent = () => {
    // Invalid integration name
    if (!backendIntegrationName) {
      return (
        <div className="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          <p>{t("integrations.integrationNotFound")}</p>
        </div>
      );
    }

    // Loading state for integrations
    if (integrationsQuery.isPending) {
      return (
        <div className="flex h-full w-full items-center justify-center p-6">
          <Loader2 className="size-16 animate-spin text-muted-foreground" />
        </div>
      );
    }

    // Loading modules
    if (modulesQuery.isPending) {
      return <ModuleCardSkeletonGrid />;
    }
    
    // Error loading modules - check if it's a "not found" error
    if (modulesQuery.isError) {
      const error = modulesQuery.error as { response?: { data?: { error?: string; code?: string } }; message?: string };
      const errorMessage = error?.response?.data?.error || error?.message;
      const isNotFound = error?.response?.data?.code?.includes("NotFound") || 
                         errorMessage?.toLowerCase().includes("not found");
      
      return (
        <div className="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          <p>{isNotFound ? t("integrations.integrationNotFound") : t("integrations.modules.failedToLoad")}</p>
        </div>
      );
    }

    // No modules available
    if (!sortedModules || sortedModules.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          <p>{t("integrations.modules.noModulesAvailable")}</p>
        </div>
      );
    }

    // Render modules grid
    return (
      <div className="grid w-full grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-8 items-stretch">
        {sortedModules.map((module: IntegrationModule) => {
          const cardData = transformModuleToCardData(module);
          
          return (
            <ModuleCard
              key={module.id}
              module={cardData}
              moduleId={module.moduleId}
              integrationName={integration?.name || (backendIntegrationName ?? '')}
              onConfigure={handleConfigure}
            />
          );
        })}
      </div>
    );
  };

  const activeModuleCount = sortedModules.filter(m => m.isActive).length;
  const totalModuleCount = sortedModules.length;

  const titleWithLogo = integrationDisplayName ? (
    <span className="flex items-center gap-3">
      <Avatar className="h-10 w-10">
        <AvatarImage src={integration?.logoUrl} alt={integrationDisplayName} />
        <AvatarFallback className="text-base">
          {integrationDisplayName
            .split(/\s+/)
            .map((word: string) => word[0])
            .join("")
            .toUpperCase()}
        </AvatarFallback>
      </Avatar>
      {integrationDisplayName}
    </span>
  ) : undefined;

  return (
    <>
      <ModernPageLayout
        breadcrumbs={breadcrumbs}
        title={titleWithLogo}
        badge={
          integration ? (
            <Badge
              variant={getStatusBadgeType(integration.isActive)}
              size="medium"
            >
              {t(getStatusLabelKey(integration.isActive))}
            </Badge>
          ) : undefined
        }
      >
        {subtitle && (
          <p className="text-sm text-muted-foreground md:text-base -mt-2">
            {subtitle}
          </p>
        )}

        {integration && (
          <IntegrationInfoSection
            integrationName={integration.name}
            clientId={clientId}
            configurationSchemaJson={integration.configurationSchemaJson}
          />
        )}

        {sortedModules.length > 0 && (
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-foreground">
              {t("integrations.info.modules")}
            </h2>
            <Badge variant="neutral" size="small">
              {t("integrations.info.activeCount", { active: activeModuleCount, total: totalModuleCount })}
            </Badge>
          </div>
        )}

        {renderContent()}
      </ModernPageLayout>

      {/* Module Configuration Dialog */}
      {configDialog.module && (
        <ModuleConfigurationDialog
          open={configDialog.open}
          onOpenChange={(open) => setConfigDialog({ open, module: open ? configDialog.module : null })}
          module={configDialog.module}
          integrationName={integration?.name || (backendIntegrationName ?? '')}
          context={
            clientId 
              ? { level: 'client', tenantId, clientId }
              : { level: 'tenant', tenantId }
          }
        />
      )}

    </>
  );
}

export default IntegrationDetailPage;
